import { utils } from '@ps/web-poker-tile-mappers';
import type { TournamentState } from '@ps/web-poker-tile-mappers';
import produce from 'immer';

import type { TournamentRaw, TournamentsUpdatePayload } from '../../../types/MTT';
import {
  formatMTTournamentAttr,
  formatTournament,
  normalizeTournamentsData
} from '../../../utils/MTT';
import type { PokerMultiTableTournamentsStateBase as State } from '../types';

const updateTournament = <T extends State>(
  state: T,
  { tournaments, locale, timeZone, siteMask, isSharedLiquidityEnabled }: TournamentsUpdatePayload
) =>
  produce(state, (draft) => {
    draft.isLoading = false;
    for (const tournament of tournaments) {
      const { __typename: typeName } = tournament;
      switch (typeName) {
        case 'Initial': {
          const initialTournaments = tournaments as never as TournamentRaw[];
          const normalizedData = normalizeTournamentsData(
            initialTournaments,
            locale,
            timeZone,
            siteMask,
            isSharedLiquidityEnabled
          );
          draft.tournamentsData = normalizedData.tournamentsData;
          draft.tournamentsCount = normalizedData.tournamentsCount;
          draft.tournamentsCurrency = normalizedData.tournamentsCurrency;
          return draft;
        }
        case 'Create':
          draft.tournamentsData[tournament.id] = formatTournament(
            tournament,
            locale,
            timeZone,
            siteMask,
            isSharedLiquidityEnabled
          );
          draft.tournamentsCount += 1;
          break;
        case 'Delete':
          delete draft.tournamentsData?.[tournament.id];
          draft.tournamentsCount -= 1;
          break;
        case 'Update': {
          const tournamentData = draft.tournamentsData?.[tournament.id];
          if (tournamentData) {
            const newTournament = { ...tournamentData, ...tournament };
            newTournament.formattedState = utils.mttUtils.getMttState(
              newTournament.legacyStatus,
              newTournament.isRestricted,
              newTournament.tournFlagsServInt
            ) as TournamentState;
            const formattedAttr = formatMTTournamentAttr(
              newTournament,
              locale,
              newTournament.isPlayMoney,
              timeZone
            );
            draft.tournamentsData[tournament.id] = {
              ...newTournament,
              ...formattedAttr
            };
          }
          break;
        }
      }
    }
    return draft;
  });

export default updateTournament;
